"use client";

import { RefObject } from "react";
import { AnimatePresence, motion } from "framer-motion";

type PriceFilterProps = {
    minPrice: string;
    maxPrice: string;
    onMinPriceChange: (val: string) => void;
    onMaxPriceChange: (val: string) => void;
    onApply: () => void;
    onClear: () => void;
    isOpen: boolean;
    onToggle: () => void;
    containerRef: RefObject<HTMLDivElement | null>;
    /** Whether to show the caret chevron (desktop has it, mobile doesn't) */
    showCaret?: boolean;
    /** Position for the dropdown */
    dropdownAlign?: "left" | "right";
};

export function PriceFilter({
    minPrice,
    maxPrice,
    onMinPriceChange,
    onMaxPriceChange,
    onApply,
    onClear,
    isOpen,
    onToggle,
    containerRef,
    showCaret = true,
    dropdownAlign = "left",
}: PriceFilterProps) {
    const hasActiveFilter = minPrice !== "" || maxPrice !== "";

    const label = hasActiveFilter
        ? `${minPrice ? `\u20B9${minPrice}` : "\u20B90"}${maxPrice ? `\u2013\u20B9${maxPrice}` : "+"}`
        : "Price";

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={onToggle}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                className="flex items-center gap-2 rounded-full border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] px-3 py-2 text-sm font-semibold text-[var(--hopp-text)] shadow-[0_8px_16px_rgba(3,7,18,0.35)] transition hover:border-[var(--hopp-sapphire)]"
            >
                <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4 text-[var(--hopp-sapphire)]"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <line x1="12" y1="1" x2="12" y2="23" />
                    <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                </svg>
                {label}
                {showCaret && (
                    <svg
                        viewBox="0 0 24 24"
                        className="h-3 w-3 text-[var(--hopp-muted)]"
                        fill="currentColor"
                    >
                        <path d="M7 10l5 5 5-5H7z" />
                    </svg>
                )}
            </button>
            <AnimatePresence>
                {isOpen ? (
                    <motion.div
                        className={`absolute ${dropdownAlign === "left" ? "left-0" : "right-0"} top-full z-[60] mt-2 w-64 rounded-2xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] p-4 shadow-[0_20px_40px_rgba(4,8,20,0.55)]`}
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.15 }}
                        role="dialog"
                        aria-label="Price range filter"
                    >
                        <div className="mb-3 text-xs font-medium text-[var(--hopp-muted)]">
                            Price range (INR)
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="flex-1">
                                <label className="sr-only" htmlFor="price-min">
                                    Minimum price
                                </label>
                                <div className="flex items-center rounded-xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] px-3 py-2 focus-within:border-[var(--hopp-sapphire)] transition">
                                    <span className="mr-1 text-xs text-[var(--hopp-muted)]">
                                        {"\u20B9"}
                                    </span>
                                    <input
                                        id="price-min"
                                        type="number"
                                        inputMode="numeric"
                                        min="0"
                                        placeholder="Min"
                                        value={minPrice}
                                        onChange={(e) => onMinPriceChange(e.target.value)}
                                        className="w-full bg-transparent text-sm text-[var(--hopp-text)] placeholder:text-[var(--hopp-muted)] outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                    />
                                </div>
                            </div>
                            <span className="text-xs text-[var(--hopp-muted)]">{"\u2013"}</span>
                            <div className="flex-1">
                                <label className="sr-only" htmlFor="price-max">
                                    Maximum price
                                </label>
                                <div className="flex items-center rounded-xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] px-3 py-2 focus-within:border-[var(--hopp-sapphire)] transition">
                                    <span className="mr-1 text-xs text-[var(--hopp-muted)]">
                                        {"\u20B9"}
                                    </span>
                                    <input
                                        id="price-max"
                                        type="number"
                                        inputMode="numeric"
                                        min="0"
                                        placeholder="Max"
                                        value={maxPrice}
                                        onChange={(e) => onMaxPriceChange(e.target.value)}
                                        className="w-full bg-transparent text-sm text-[var(--hopp-text)] placeholder:text-[var(--hopp-muted)] outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                            {hasActiveFilter && (
                                <button
                                    type="button"
                                    onClick={onClear}
                                    className="flex-1 rounded-xl px-3 py-2 text-sm text-[var(--hopp-muted)] transition hover:bg-[var(--hopp-panel-strong)] hover:text-[var(--hopp-text)]"
                                >
                                    Clear
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={onApply}
                                className="flex-1 rounded-xl bg-[var(--hopp-sapphire)] px-3 py-2 text-sm font-semibold text-white transition hover:opacity-90"
                            >
                                Apply
                            </button>
                        </div>
                    </motion.div>
                ) : null}
            </AnimatePresence>
        </div>
    );
}
